import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useClaimPriority } from "@/hooks/useClaimPriority";
import { ApiError } from "@/lib/apiClient";
import { getApiErrorMessage, getAssignmentLimitMessage } from "@/lib/priority-errors";
import type { Priority } from "@/types";

/** Backend error code: someone claimed it first. `details` = that DRI's full name. */
const PRIORITY_ALREADY_HAS_DRI = "PRIORITY_ALREADY_HAS_DRI";

function claimErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { message?: string; details?: string };
    if (body?.message === PRIORITY_ALREADY_HAS_DRI) {
      const name = body.details?.trim();
      return name ? `${name} already owns this deliverable — ask them if you want to take it over.` : "Someone already owns this deliverable.";
    }
  }
  return getAssignmentLimitMessage(error) ?? getApiErrorMessage(error) ?? "Could not claim this deliverable.";
}

const hasDri = (p: Priority) => !!p.dri || (p.assignments ?? []).some((a) => a.isDri);

interface ClaimCandidateButtonProps {
  priority: Priority;
  className?: string;
}

/** "Claim as DRI" on a candidate: anyone in the company may take an unowned deliverable. */
export function ClaimCandidateButton({ priority, className }: ClaimCandidateButtonProps) {
  const claim = useClaimPriority();
  if (!priority.isCandidate || hasDri(priority)) return null;

  return (
    <Button
      size="sm"
      variant="outline"
      className={className}
      disabled={claim.isPending}
      onClick={(e) => {
        e.stopPropagation();
        claim.mutate(
          { pid: priority.pid, asDri: true },
          {
            onSuccess: () => toast.success(`You are now the DRI of ${priority.code}.`),
            onError: (error) => toast.error("Could not claim", { description: claimErrorMessage(error) }),
          },
        );
      }}
    >
      <UserPlus className="h-3.5 w-3.5" /> Claim as DRI
    </Button>
  );
}
